import { useMutation } from '@vue/apollo-composable'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { RouteRecordNormalized, RouteRecordRaw } from 'vue-router'

import {
  type Boot_AuthMeFragment,
  StoreAuth_AuthLogoutDocument,
  UserType,
} from '@/gql/graphql'
import type { FactoryId } from '@/types/formStepsFactory'

export const useAuthStore = defineStore(
  'auth',
  () => {
    const { mutate: mutateAuthLogout } = useMutation(
      StoreAuth_AuthLogoutDocument,
    )

    const dialogAuth = ref<boolean>(false)

    const dialogFactoryId = ref<FactoryId>('auth')

    const notificationDismissDeleteRequest = ref<boolean>(false)

    const routeRedirect = ref<RouteRecordNormalized | RouteRecordRaw>()

    const user = ref<Boot_AuthMeFragment | null>()

    const userHasCompletedSetup = computed<boolean>(
      () => !!user.value?.completed_setup_at,
    )

    const role = computed<UserType>(() => user.value?.type ?? 'GUEST')

    const logout = async () => {
      user.value = null

      try {
        await mutateAuthLogout()
      } catch (e) {
        console.error(e)
      } finally {
        const csrfCookieUrl = process.env.API_BASE_URL + '/sanctum/csrf-cookie'

        await fetch(csrfCookieUrl, {
          credentials: 'include',
          headers: {
            accept: 'application/json',
            'x-requested-with': 'XMLHttpRequest',
          },
        })
      }
    }

    const setRouteRedirect = (
      route: RouteRecordNormalized | RouteRecordRaw,
    ) => {
      routeRedirect.value = route
    }

    return {
      dialogAuth,
      dialogFactoryId,
      logout,
      notificationDismissDeleteRequest,
      role,
      routeRedirect,
      setRouteRedirect,
      user,
      userHasCompletedSetup,
    }
  },
  {
    share: {
      enable: !!process.env.CLIENT,
      initialize: !!process.env.CLIENT,
      omit: [
        'dialogAuth',
        'dialogFactoryId',
        'notificationDismissDeleteRequest',
        'routeRedirect',
      ],
    },
  },
)

if (import.meta.hot && process.env.NODE_ENV === 'development') {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
